import { useEffect, useState } from 'react';
import { ActivityIndicator, Pressable } from 'react-native';
import { useTheme } from '@shopify/restyle';
import { useAtomValue, useStore } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { RemoteError } from 'app/design/primitives/remote-error';
import { isLoggingOutAtom, userInfoAtom } from 'app/atoms';
import { useNavigation } from 'app/hooks/navigation';
import { useIsExpired } from 'app/hooks/use-is-expired';
import { useAllAddresses } from 'app/network/address';
import { useListingVisibilityGroups } from 'app/network/groups';
import { useAllListings, useCreateListing, useOneListing } from 'app/network/listing';
import { useUser } from 'app/network/user';
import { StorageKey, useStoredValue } from 'app/lib/storage';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { ListingMatchStatus, ListingSubtype, ListingType, TransactionMode, type Listing } from 'app/types/listing';
import type { ListingsVisibilityExclusionsPreference } from 'app/screens/new';
import type { Theme } from 'app/shopify';
import { HeaderSection } from './header-section';
import { getConciergeVisibility } from './request-helpers';
import {
  groupedRequestsAtom,
  queueGroupedRequest,
  undoQueuedRequest,
  UNDO_DURATION_MS,
  type GroupedRequestState,
} from './request-queue';
import { RequestProgressFill } from './request-progress-fill';

export function GroupedListingSummary({ listing, isGuest = false }: { listing: Listing; isGuest?: boolean }) {
  const { data: user } = useUser();
  const eligible =
    !isGuest &&
    user &&
    isFeatureEnabled(FeatureFlag.conciergerie, user.featureFlags) &&
    isFeatureEnabled(FeatureFlag.publishListings, user.featureFlags) &&
    listing.type === ListingType.offer &&
    listing.subtype === ListingSubtype.object &&
    !listing.isDonation &&
    (!listing.transactionMode || listing.transactionMode === TransactionMode.lend);
  if (!eligible) return <HeaderSection {...listing} conciergeCtaEnabled />;
  return <RequestSummaryContent key={user.id + listing.item.name} listing={listing} userId={user.id} />;
}

function RequestAction({
  label,
  onPress,
  disabled = false,
  busy = false,
  primary = false,
  testID,
  hint,
  countdown,
}: {
  label: string;
  onPress: () => void;
  disabled?: boolean;
  busy?: boolean;
  primary?: boolean;
  testID?: string;
  hint?: string;
  countdown?: { deadline: number; now: number };
}) {
  const theme = useTheme<Theme>();
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityHint={hint}
      accessibilityState={{ busy, disabled }}
      style={({ pressed }) => ({
        minHeight: 48,
        justifyContent: 'center',
        paddingHorizontal: primary ? theme.spacing['spacing.general.x4'] : theme.spacing['spacing.general.x2'],
        paddingVertical: theme.spacing['spacing.general.x2'],
        borderRadius: theme.borderRadii['radius.full'],
        overflow: 'hidden',
        backgroundColor: primary ? theme.colors[countdown ? 'surface.primary' : 'surface.secondary'] : undefined,
        opacity: countdown ? 1 : disabled ? 0.55 : pressed ? 0.6 : 1,
      })}
    >
      {countdown ? <RequestProgressFill {...countdown} /> : null}
      <Row alignItems="center" justifyContent="center" gap="spacing.general.x2">
        <Text
          variant="bodyBase"
          color="text.primary"
          fontWeight="600"
          textAlign="center"
          flexShrink={1}
          style={{ fontVariant: ['tabular-nums'] }}
        >
          {label}
        </Text>
        {busy ? <ActivityIndicator size="small" color={theme.colors['text.primary']} /> : null}
      </Row>
    </Pressable>
  );
}

function isRetainedRequest(request?: Listing): request is Listing {
  return Boolean(request && !['archived', 'deleted', 'expired'].includes(request.status));
}

function useGroupedRequest({ listing, userId }: { listing: Listing; userId: string }) {
  const { t } = useTranslation('grouped-listing');
  const navigation = useNavigation();
  const authorizeRequest = useIsExpired();
  const addresses = useAllAddresses();
  const groups = useListingVisibilityGroups();
  const [exclusions] = useStoredValue<ListingsVisibilityExclusionsPreference | null>(
    StorageKey.ListingsVisibilityExclusionsPreference,
    {},
  );
  const store = useStore();
  const sessions = useAtomValue(groupedRequestsAtom);
  const key = JSON.stringify([userId, listing.categoryPrimary.id, listing.item.name.toLocaleLowerCase()]);
  const session = sessions[key];
  const creation = useCreateListing();
  const { existingRequests, request } = useExistingGroupedRequest({ listing, userId, key, session, store });
  const queued = session?.phase === 'queued';
  const sending = session?.phase === 'sending';
  const { paused, found, hasOutreach, contacted } = getRequestStatus(request);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!queued) return;
    const interval = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(interval);
  }, [queued]);
  const seconds = Math.min(UNDO_DURATION_MS / 1000, Math.max(1, Math.ceil(((session?.deadline ?? now) - now) / 1000)));
  const loadError = addresses.error || groups.error || existingRequests.error;
  const error = session?.error || loadError;
  const ready = Boolean(addresses.data && groups.data && exclusions !== undefined && !existingRequests.isPending);
  const busy = sending || (!ready && !loadError);

  const publish = () =>
    authorizeRequest(() => {
      if (!ready || queued || sending || request || !addresses.data || !groups.data || exclusions === undefined) return;
      const visibility = getConciergeVisibility(addresses.data, groups.data, exclusions);
      const payload = {
        ...visibility,
        type: ListingType.request,
        subtype: ListingSubtype.object,
        item: { name: listing.item.name },
        categoryPrimaryId: listing.categoryPrimary.id,
        serviceCategoryId: null,
        description: null,
        start: null,
        end: null,
        picture: null,
        automaticMessage: t('concierge.message', { item: listing.item.name }),
        source: 'grouped_listing_concierge' as const,
      };
      if (!visibility.addressIds.length && !visibility.groupIds.length) {
        navigation.navigate(
          'new',
          {
            defaultValues: {
              type: payload.type,
              subtype: payload.subtype,
              item: payload.item,
              categoryPrimaryId: payload.categoryPrimaryId,
              automaticMessage: payload.automaticMessage,
            },
            newListingSource: payload.source,
          },
          { pop: true },
        );
        return;
      }
      setNow(Date.now());
      queueGroupedRequest(
        store,
        key,
        () => creation.mutateAsync(payload),
        () => !store.get(isLoggingOutAtom) && store.get(userInfoAtom)?.id === userId,
      );
    });

  return {
    request,
    session,
    queued,
    sending,
    paused,
    found,
    hasOutreach,
    now,
    seconds,
    loadError,
    error,
    busy,
    contacted,
    publish,
    viewRequest: () => {
      if (request) navigation.navigate('one-listing', { id: request.id }, { pop: true });
    },
    undo: () => undoQueuedRequest(store, key),
    retry: () => {
      void addresses.refetch();
      void groups.refetch();
      void existingRequests.refetch();
    },
  };
}

function RequestSummaryContent({ listing, userId }: { listing: Listing; userId: string }) {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('grouped-listing');
  const { t: tAPI } = useTranslation('api-errors');
  const state = useGroupedRequest({ listing, userId });
  const { request, session, error, contacted, viewRequest } = state;
  const copy = getRequestCopy(state);

  return (
    <Column
      gap="spacing.general.x4"
      padding="spacing.general.x4"
      testID="grouped-concierge-card"
      style={{ borderRadius: theme.borderRadii['radius.lg'], backgroundColor: theme.colors['surface.primary-neutral'] }}
    >
      <HeaderSection {...listing} conciergeCtaEnabled />
      <Column
        gap="spacing.general.x2"
        style={{
          borderTopWidth: 1,
          borderTopColor: theme.colors['border.secondary'],
          paddingTop: theme.spacing['spacing.general.x4'],
        }}
      >
        <Text variant="bodyBase" fontWeight="600" accessibilityLiveRegion="polite">
          {t(copy.title)}
        </Text>
        <Text variant="bodySmall" color="text.secondary" accessibilityLiveRegion="polite">
          {t(copy.description)}
        </Text>
        {request ? (
          <>
            {contacted !== undefined ? (
              <Text variant="bodySmall" color="text.secondary">
                {t('concierge.contacted', { count: contacted })}
              </Text>
            ) : null}
            <RequestAction label={t('concierge.viewRequest')} primary onPress={viewRequest} />
          </>
        ) : (
          <RequestPublishActions {...state} />
        )}
        {session?.phase === 'cancelled' && !request ? (
          <Text variant="bodySmall" color="text.secondary" accessibilityLiveRegion="polite">
            {t('concierge.undone')}
          </Text>
        ) : null}
        {error ? <RemoteError error={error} message={tAPI(error.message)} /> : null}
      </Column>
    </Column>
  );
}

function useExistingGroupedRequest({
  listing,
  userId,
  key,
  session,
  store,
}: {
  listing: Listing;
  userId: string;
  key: string;
  session?: GroupedRequestState;
  store: ReturnType<typeof useStore>;
}) {
  const existingRequests = useAllListings(
    {
      userId,
      type: ListingType.request,
      search: listing.item.name,
      category: listing.categoryPrimary.id,
      doGrouping: false,
    },
    'grouped-listing-request',
    { refetchOnMount: 'always', refetchInterval: 30_000 },
  );
  const existing = existingRequests.data?.pages
    .flatMap((page) => page.results)
    .find(
      (request) =>
        request.item.name.toLocaleLowerCase() === listing.item.name.toLocaleLowerCase() &&
        request.categoryPrimary.id === listing.categoryPrimary.id &&
        isRetainedRequest(request) &&
        request.id !== (session?.phase === 'cancelled' ? session.request?.id : undefined),
    );
  const requestId = isRetainedRequest(session?.request) ? session.request.id : existing?.id;
  const detail = useOneListing(requestId ?? '', 'grouped-request-controls', {
    enabled: Boolean(requestId),
    refetchInterval: 30_000,
  });
  const latest = detail.data ?? session?.request ?? existing;
  const request = isRetainedRequest(latest) ? latest : undefined;
  useEffect(() => {
    if (
      session?.phase !== 'live' ||
      !detail.data ||
      detail.data.id !== session.request?.id ||
      isRetainedRequest(detail.data)
    )
      return;
    store.set(groupedRequestsAtom, (previous) => {
      if (previous[key] !== session) return previous;
      const next = { ...previous };
      delete next[key];
      return next;
    });
  }, [detail.data, key, session, store]);
  return { existingRequests, request };
}

function getRequestCopy({
  request,
  found,
  paused,
  sending,
  hasOutreach,
}: {
  request?: Listing;
  found: boolean;
  paused: boolean;
  sending: boolean;
  hasOutreach: boolean;
}) {
  const title = request
    ? found
      ? 'concierge.found'
      : paused
        ? 'concierge.pausedTitle'
        : 'concierge.successTitle'
    : sending
      ? 'concierge.publishing'
      : 'concierge.title';
  const description = request
    ? found
      ? 'concierge.foundDescription'
      : paused
        ? 'concierge.pausedDescription'
        : hasOutreach
          ? 'concierge.activeDescription'
          : 'concierge.waitingDescription'
    : 'concierge.description';

  return { title, description };
}

function getRequestStatus(request?: Listing) {
  const paused = request?.conciergerieDisabledByUser === true;
  const found = Boolean(
    request?.status === 'loaned' ||
    request?.status === 'was_loaned' ||
    request?.lender ||
    request?.needMatches?.some((match) => match.status === ListingMatchStatus.MATCH),
  );
  const hasOutreach =
    request?.type === ListingType.request && !request.conciergerieOff && Boolean(request.automaticMessage?.trim());
  const contacted = request?.type === ListingType.request ? request.contactedMembers : undefined;
  return { paused, found, hasOutreach, contacted };
}

function RequestPublishActions({
  queued,
  sending,
  seconds,
  loadError,
  busy,
  session,
  now,
  retry,
  publish,
  undo,
}: ReturnType<typeof useGroupedRequest>) {
  const { t } = useTranslation('grouped-listing');
  return (
    <>
      <RequestAction
        label={
          queued
            ? t('concierge.queued', { count: seconds })
            : t(sending ? 'concierge.publishing' : loadError ? 'concierge.retry' : 'concierge.action')
        }
        hint={t('concierge.description')}
        primary
        busy={busy}
        disabled={busy || queued}
        countdown={queued && session?.deadline ? { deadline: session.deadline, now } : undefined}
        testID="grouped-concierge-publish"
        onPress={loadError ? retry : publish}
      />
      {queued ? (
        <Row justifyContent="flex-end" testID="grouped-request-undo">
          <RequestAction label={t('concierge.undo')} onPress={undo} testID="grouped-request-undo-action" />
        </Row>
      ) : null}
    </>
  );
}
